import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getDetailedUser } from '../../../actions/users';
import { editEmployerUser, clearEmployerDetails } from '../../../actions/employers';
import { getCompanyDetailsAction, getCompanyDepartments } from '../../../actions/companies';
import { GET_EMPLOYER_DETAILS_SUCCESS } from '../../../actions/actionTypes';
import { startSupportConversation } from '../../../actions/conversations';
import { visuallyFormatPhoneNumber } from '../../../helpers/general';
import { modifyObject } from '../../../helpers/modifyObject';
import { appPath } from '../../../constants';
import { getUserDepartment } from '../../../helpers/companies';
import FormEmployerDetails from '../../common/forms/FormEmployerDetails';
import _ from 'lodash';
import Validator from '../../common/forms/Validator';
import { UIShowConfirm } from '../../../actions/ui';
import SwitchRow from '../../common/forms/SwitchRow';
import BackArrow from '../../common/ui/BackArrow';

class EmployerDetails extends Validator {
  static propTypes = {
    employerDetails: PropTypes.object.isRequired,
    company: PropTypes.object.isRequired,
    department: PropTypes.object,
    getDetailedUser: PropTypes.func.isRequired,
    clearEmployerDetails: PropTypes.func.isRequired,
    editEmployerUser: PropTypes.func.isRequired,
    startSupportConversation: PropTypes.func.isRequired,
    getCompanyDetailsAction: PropTypes.func.isRequired,
    getCompanyDepartments: PropTypes.func.isRequired
  };

  state = {
    employerDetails: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      department_id: null
    },
    initialEmployerDetails: {},
    isEditingPersonalInfo: false,
    isLoadingPersonalInfo: false,
    employerNewPhotoUrl: ''
  };

  validationRules = {
    email: { email: true },
    first_name: { required: true },
    last_name: { required: true },
    phone: { required: true }
  };

  componentDidMount () {
    const { getDetailedUser, getCompanyDetailsAction, getCompanyDepartments, match: { params: { id } }, history } = this.props;
    if (isNaN(id)) {
      history.push(`/${appPath}/employers`);
    } else {
      getDetailedUser(id, GET_EMPLOYER_DETAILS_SUCCESS)
        .then(() => {
          const id = this.props.employerDetails.company_id;

          getCompanyDepartments(id);
          getCompanyDetailsAction(id);
        });
    }
  }

  componentWillUnmount () {
    this.props.clearEmployerDetails();
  };

  static getDerivedStateFromProps = (props, state) => {
    if (!_.isEqual(props.employerDetails, state.initialEmployerDetails)) {
      return {
        employerDetails: props.employerDetails,
        initialEmployerDetails: props.employerDetails
      };
    }
    // Return null to indicate no change to state.
    return null;
  };

  _validate = () => {
    if (this._checkDepartments()) {
      this.validationRules.department_id = { required: true };
    }

    return this.validate(this.validationRules, this.state.employerDetails);
  };

  _checkDepartments = () => {
    return this.state.employerDetails.is_company && this.props.company.departments.length;
  };

  _startConversation = () => {
    const { id } = this.state.employerDetails;
    this.props.startSupportConversation(id).then(data => {
      if (data) {
        window.open(`/${appPath}/conversation/${data.conversation_id}`, '_blank', 'height=600, width=500');
      }
    });
  };

  changeEmployerActivity = () => {
    const is_active = !this.state.employerDetails.is_active;

    this.props.UIShowConfirm(
      'Are you sure you want to change employer activity?',
      () => {
        this.props.editEmployerUser(this.state.employerDetails.id, { is_active });
      }
    );
  };

  enableEditingPersonalInfo = () => {
    this.setState({
      isEditingPersonalInfo: true
    });
  };

  handleChangeEmployer = event => {
    const { name, value } = event.target;
    this.setState((prevState) => ({
      employerDetails: {
        ...prevState.employerDetails,
        [name]: value
      }
    }));
  };

  handleChangeEmployerPhone = event => {
    const { value } = event.target;
    this.setState((prevState) => ({
      employerDetails: {
        ...prevState.employerDetails,
        phone: visuallyFormatPhoneNumber(value)
      }
    }));
  };

  handleChangeSelect = event => {
    const { name, value } = event.target;
    this.setState({
      employerDetails: {
        ...this.state.employerDetails,
        [name]: parseInt(value, 10)
      }
    });
  };

  cancelEditingPersonalInfo = () => {
    this._resetErrors();
    this.setState({
      employerDetails: this.state.initialEmployerDetails,
      employerNewPhotoUrl: '',
      isEditingPersonalInfo: false
    });
  };

  submitEditingPersonalInfo = () => {
    if (!this._validate()) {
      return;
    }

    const { editEmployerUser } = this.props;
    const { employerDetails: { id, first_name, last_name, email, phone, department_id } } = this.state;

    this.setState({ isLoadingPersonalInfo: true });
    const employerData = modifyObject({
      first_name,
      last_name,
      email,
      phone,
      department_id
    });
    editEmployerUser(id, employerData).then(this._finishEditing);
  };

  handleChangePassword = (password, temp_password) => {
    const { editEmployerUser } = this.props;
    const { id } = this.state.employerDetails;
    return editEmployerUser(id, { password, temp_password });
  };

  _finishEditing = () => this.setState({
    isLoadingPersonalInfo: false,
    isEditingPersonalInfo: false
  });

  render () {
    const { company, department } = this.props;
    const { employerDetails, initialEmployerDetails, isLoadingPersonalInfo, isEditingPersonalInfo } = this.state;
    const { first_name, last_name } = initialEmployerDetails;
    const errors = this.validationErrors;
    return (
      <div className='user-details-wrapper employer-details'>
        <div className='user-header-wrapper mb-4'>
          <div className='d-flex align-items-center'>
            <BackArrow
              history={this.props.history}
              location={this.props.location}
              defaultBackPath={`/${appPath}/companies`}
            />
            <h1 className='h1 m-0'>{first_name} {last_name}</h1>
          </div>

          <SwitchRow checked={employerDetails.is_active || false} onChange={this.changeEmployerActivity} label='Is Active' />
        </div>

        <button className='button button-primary' onClick={this._startConversation}>Send Message</button>

        <main className='main-wrapper-details-blocks mt-4'>
          <FormEmployerDetails
            isEditing={isEditingPersonalInfo}
            enableEditingPersonalInfo={this.enableEditingPersonalInfo}
            userDetails={employerDetails}
            handleChangeUser={this.handleChangeEmployer}
            handleChangeUserPhone={this.handleChangeEmployerPhone}
            cancelEditingPersonalInfo={this.cancelEditingPersonalInfo}
            savePersonalInfo={this.submitEditingPersonalInfo}
            isLoadingPersonalInfo={isLoadingPersonalInfo}
            handleChangeSelect={this.handleChangeSelect}
            company={company}
            department={department}
            handleChangePassword={this.handleChangePassword}
            errors={errors}
          />
        </main>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const employerDetails = state.employers.employerDetails;
  const company = state.companies.companyDetails;

  return {
    employerDetails, company, department: getUserDepartment(employerDetails, company.departments)
  };
};

const mapDispatchToProps = {
  getDetailedUser,
  clearEmployerDetails,
  editEmployerUser,
  getCompanyDetailsAction,
  startSupportConversation,
  getCompanyDepartments,
  UIShowConfirm
};

export default connect(mapStateToProps, mapDispatchToProps)(EmployerDetails);



// WEBPACK FOOTER //
// ./src/components/app/employerDetails/index.js